import React, {Fragment, useEffect, useState} from "react";
import {Link, LoaderFunctionArgs, useLoaderData, useNavigate} from "react-router-dom";
import axios from "axios";
import {EntityMonstrousAbility, MonstrousAbility, MonsterTypeDetail} from "../../server/monster";
import {
    Autocomplete,
    Button,
    ButtonGroup,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    IconButton,
    Paper,
    TextField,
    Tooltip
} from "@mui/material";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DeleteIcon from "@mui/icons-material/Delete";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import {CreateMonstrousAbilityDialog} from "./components/CreateMonstrousAbilityDialog";
import {useMonsterCatalog} from "./contexts/MonsterCatalogContext";

export const monsterTypeLoader = async ({params}: LoaderFunctionArgs): Promise<MonsterTypeDetail | null> => {
    if (params.id) {
        return axios.get(`/monsters/type/${params.id}`).then(({data}) => data)
    }
    return null
}

const monsterColumns: GridColDef[] = [
    {
        field: 'name', headerName: 'Monster', headerAlign: "center", flex: 1,
        renderCell: (params) => <Link to={`/monsters/${params.row.id}`}>{params.value}</Link>
    },
    {
        field: 'ace', headerName: 'ACE', headerAlign: "center", align: "center", width: 120,
        valueGetter: (params) => params.row.attributes?.ace ?? ''
    },
    {
        field: 'unique', headerName: 'Unique', headerAlign: "center", align: "center", width: 80,
        renderCell: (params) => params.value ? '✓' : ''
    },
    {
        field: 'dedicated_marshal', headerName: 'Ded. Marshal', headerAlign: "center", align: "center", width: 120,
        renderCell: (params) => params.value ? <Chip label="Ded. Marshal" color="secondary" size="small"/> : ''
    },
]

function abilityChipColor(type: string): 'primary' | 'success' | 'error' {
    if (type === 'IMMUNITY') return 'primary'
    if (type === 'STRENGTH') return 'success'
    return 'error'
}

export function MonsterTypeViewer() {
    const [type, setType] = useState(useLoaderData() as MonsterTypeDetail)
    const [editable, setEditable] = useState(false)
    const {monstrousAbilities: catalog, refreshMonstrousAbilities} = useMonsterCatalog()
    const [selectedAbility, setSelectedAbility] = useState<MonstrousAbility | null>(null)
    const [createDialogOpen, setCreateDialogOpen] = useState(false)
    const navigate = useNavigate()

    async function saveType() {
        await axios.put(`/monsters/type/${type.id}`, type).then(({data}) => {
            setType({...data, Monster: type.Monster, monstrousAbilities: type.monstrousAbilities})
            setEditable(false)
        })
    }

    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            if (e.ctrlKey && e.key === 'e') { e.preventDefault(); setEditable(prev => !prev) }
            else if (e.ctrlKey && e.key === 's') { e.preventDefault(); if (editable) saveType() }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [editable])

    async function addAbility() {
        if (!selectedAbility) return
        const {data} = await axios.post(`/monsters/type/${type.id}/monstrous-abilities`, {abilityId: selectedAbility.id})
        setType({...type, monstrousAbilities: [...(type.monstrousAbilities ?? []), data]})
        setSelectedAbility(null)
    }

    async function removeAbility(entryId: number) {
        await axios.delete(`/monsters/type/${type.id}/monstrous-abilities/${entryId}`)
        setType({...type, monstrousAbilities: (type.monstrousAbilities ?? []).filter(a => a.id !== entryId)})
    }

    async function handleAbilityCreated(ability: MonstrousAbility) {
        refreshMonstrousAbilities()
        const {data} = await axios.post(`/monsters/type/${type.id}/monstrous-abilities`, {abilityId: ability.id})
        setType({...type, monstrousAbilities: [...(type.monstrousAbilities ?? []), data]})
    }

    return <Fragment>
        <Grid container spacing={1}>

            {/* Header */}
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography align="center" variant="h4" component="h4">{type.name}</Typography>
                    <Container sx={{textAlign: 'center'}}>
                        <ButtonGroup variant="contained" size="small">
                            <Button title="Edit" onClick={() => setEditable(true)} disabled={editable}><EditIcon/></Button>
                            <Button title="Save" disabled={!editable} onClick={saveType}><SaveIcon/></Button>
                        </ButtonGroup>
                    </Container>
                </Paper>
            </Grid>

            {/* Type Details */}
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Type Details</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Name" disabled={!editable}
                                       value={type.name}
                                       onChange={(e) => setType({...type, name: e.target.value})}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Description" disabled={!editable} multiline minRows={3}
                                       value={type.description ?? ''}
                                       onChange={(e) => setType({...type, description: e.target.value})}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Combat Role" disabled={!editable}
                                       value={type.combat_role ?? ''}
                                       onChange={(e) => setType({...type, combat_role: e.target.value})}/>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox
                                    checked={type.general_use}
                                    disabled={!editable}
                                    onChange={(e) => setType({...type, general_use: e.target.checked})}
                                />}
                                label="General Use"/>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Species */}
            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Species</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Species Name" disabled
                                       value={type.species?.name ?? ''}
                                       InputProps={{
                                           endAdornment: type.species?.id
                                               ? <Link to={`/monsters/species/${type.species.id}`} style={{whiteSpace: 'nowrap', paddingLeft: 8}}>View</Link>
                                               : undefined
                                       }}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Species Description" disabled multiline minRows={4}
                                       value={type.species?.description ?? ''}/>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Costuming */}
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Costuming</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={3}>
                            <TextField fullWidth label="Maximum Fieldable" type="number" disabled={!editable}
                                       value={type.maximum_fieldable ?? ''}
                                       inputProps={{min: 0}}
                                       onChange={(e) => setType({
                                           ...type,
                                           maximum_fieldable: e.target.value ? parseInt(e.target.value) : null
                                       })}/>
                        </Grid>
                        <Grid item xs={12} sm={3} sx={{display: 'flex', alignItems: 'center'}}>
                            <FormControlLabel
                                control={<Checkbox
                                    checked={type.latex_based ?? false}
                                    disabled={!editable}
                                    onChange={(e) => setType({...type, latex_based: e.target.checked})}
                                />}
                                label="Latex Based"/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Costuming Requirements" disabled={!editable} multiline minRows={4}
                                       value={type.costuming_requirements ?? ''}
                                       onChange={(e) => setType({...type, costuming_requirements: e.target.value})}/>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Monstrous Abilities */}
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Monstrous Abilities</Typography>
                    <Grid container spacing={1} sx={{mt: 0.5}}>

                        {/* Own abilities */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">Assigned to this Type</Typography>
                        </Grid>
                        <Grid item xs={7}>
                            <Autocomplete
                                options={catalog.filter(a => !(type.monstrousAbilities ?? []).some(e => e.ability.id === a.id))}
                                getOptionLabel={(a) => `${a.name} — ${a.call} (${a.type})`}
                                value={selectedAbility}
                                onChange={(_, value) => setSelectedAbility(value)}
                                renderInput={(params) => <TextField {...params} label="Ability" size="small"/>}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Button fullWidth variant="contained" disabled={!selectedAbility}
                                    onClick={addAbility}>Add</Button>
                        </Grid>
                        <Grid item xs={3}>
                            <Button fullWidth variant="outlined" onClick={() => setCreateDialogOpen(true)}>Create New</Button>
                        </Grid>
                        {(type.monstrousAbilities ?? []).map((entry) => (
                            <Grid item xs={12} key={entry.id} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <Chip label={entry.ability.type} color={abilityChipColor(entry.ability.type)} size="small"/>
                                <Typography variant="body2" sx={{flex: 1}}>{entry.ability.name} — {entry.ability.call}</Typography>
                                <Tooltip title="Remove"><span>
                                    <IconButton size="small" color="error" onClick={() => removeAbility(entry.id as number)}>
                                        <DeleteIcon fontSize="small"/>
                                    </IconButton>
                                </span></Tooltip>
                            </Grid>
                        ))}
                        {(type.monstrousAbilities ?? []).length === 0 && (
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">No monstrous abilities assigned to this type</Typography>
                            </Grid>
                        )}

                        {/* Inherited from species */}
                        {(type.species?.monstrousAbilities ?? []).length > 0 && <>
                            <Grid item xs={12} sx={{mt: 1}}>
                                <Typography variant="subtitle2">Inherited from Species (read-only)</Typography>
                            </Grid>
                            {(type.species.monstrousAbilities ?? []).map((entry) => (
                                <Grid item xs={12} key={entry.id} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <Chip label={entry.ability.type} color={abilityChipColor(entry.ability.type)} size="small"/>
                                    <Typography variant="body2">{entry.ability.name} — {entry.ability.call}</Typography>
                                </Grid>
                            ))}
                        </>}

                    </Grid>
                </Paper>
            </Grid>

            {/* Monsters */}
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">
                        Monsters
                        <Tooltip title="Add Monster of this Type"><span>
                            <Button onClick={() => navigate(`/monsters/new?typeId=${type.id}`)}><AddBoxIcon/></Button>
                        </span></Tooltip>
                    </Typography>
                    <DataGrid
                        autoHeight
                        rows={type.Monster ?? []}
                        columns={monsterColumns}
                        getRowId={(row) => row.id}
                        density="compact"
                        initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                    />
                </Paper>
            </Grid>

        </Grid>

        <CreateMonstrousAbilityDialog
            open={createDialogOpen}
            onClose={() => setCreateDialogOpen(false)}
            onCreated={handleAbilityCreated}
        />
    </Fragment>
}
